var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from "lit";
import { property, query, state } from "lit/decorators.js";
import { collapse, expand } from "../utils/element-collapse.js";
import { reset } from "../styles.js";
import { styles } from "./styles.js";
export var SnackbarDuration;
(function (SnackbarDuration) {
    SnackbarDuration[SnackbarDuration["Short"] = 4000] = "Short";
    SnackbarDuration[SnackbarDuration["Long"] = 10000] = "Long";
    SnackbarDuration[SnackbarDuration["Infinite"] = 120000000] = "Infinite";
})(SnackbarDuration || (SnackbarDuration = {}));
/**
 * An item to show in a `w-snackbar`.
 *
 * See the `create` function on `w-snackbar` for a convenience API which helps you make snackbar items.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/snackbar/frameworks/elements)
 *
 * @parent w-snackbar
 *
 * @slot default - the snackbar message.
 * @slot icon - icon preceeding the message.
 * @slot action - action following the message.
 *
 * @csspart item - the container wrapping the message, icon and actions.
 * @csspart icon - the icon for the variant.
 * @csspart message - the default slot, body of the snackbar item.
 * @csspart action - close and action button.
 *
 * @cssprop --w-c-snackbar-item-radius
 * @cssprop --w-c-snackbar-item-max-width
 * @cssprop --w-c-snackbar-item-min-width
 * @cssprop --w-c-snackbar-item-padding
 *
 * @cssprop --w-c-snackbar-item-bg
 * @cssprop --w-c-snackbar-item-action-bg-active
 * @cssprop --w-c-snackbar-item-action-bg-hover
 *
 * @cssprop --w-c-snackbar-item-color
 * @cssprop --w-c-snackbar-item-action-color-active
 * @cssprop --w-c-snackbar-item-action-color-hover
 *
 * @cssprop --w-c-snackbar-item-box-shadow
 */
export class WarpSnackbarItem extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The placement of the action and close buttons.
         */
        this.actionPlacement = "inline";
        /**
         * How long the message should stay in the document before removing itself.
         */
        this.duration = SnackbarDuration.Short;
        this.hasAction = false;
        this.#expanded = false;
        this.#durationLeft = SnackbarDuration.Short;
        this.#lastFocused = null;
        // TODO How to expose the timer in an accessible maner, both visually and to assistive technologies? https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/timer_role + a progress bar style thing?
        this.#timer = null;
        this.#timerPaused = false;
    }
    static { this.styles = [reset, styles]; }
    #expanded;
    #durationLeft;
    #lastFocused;
    // TODO How to expose the timer in an accessible maner, both visually and to assistive technologies? https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/timer_role + a progress bar style thing?
    #timer;
    #timerPaused;
    connectedCallback() {
        super.connectedCallback();
        if (this.duration !== SnackbarDuration.Infinite) {
            const updateFrequencyMs = 1000;
            // Subtract one updateFrequencyMs, otherwise the full
            // duration would be duration + updateFrequencyMs.
            this.#durationLeft = this.duration - updateFrequencyMs;
            this.#timer = setInterval(() => {
                if (this.#timerPaused)
                    return;
                this.#durationLeft -= updateFrequencyMs;
                if (this.#durationLeft <= 0) {
                    this.close();
                }
            }, updateFrequencyMs);
        }
    }
    disconnectedCallback() {
        if (this.#timer) {
            clearInterval(this.#timer);
        }
    }
    async updated() {
        if (!this.root)
            return; // should never happen
        // Once widely available, replace with https://developer.chrome.com/docs/css-ui/animate-to-height-auto
        if (!this.#expanded) {
            // we need to measure including the height of the child
            // components which aren't ready yet and have 0x0 dimensions,
            // wait for the update to finish
            await this.updateComplete;
            expand(this.root, () => {
                this.#expanded = true;
            });
        }
    }
    /**
     * Remove the snackbar item from the document.
     *
     * Moves focus to the last focused element outside of the snackbar item, if available.
     */
    close() {
        if (this.#lastFocused) {
            this.#lastFocused.focus();
        }
        if (this.#expanded && this.root) {
            collapse(this.root, () => {
                this.remove();
            });
        }
        else {
            this.remove();
        }
    }
    /**
     * Listen to focusin, not focus, as focus does not bubble.
     *
     * We're interested in running code when an action button gets focus,
     * such as pausing the countdown and storing the last focused element
     * outside of the snackbar so we can return there when it closes.
     *
     * Only save the first instance of `relatedTarget` so we don't overwrite
     * it when focus moves from an action button to the close button.
     */
    #onFocusIn(e) {
        if (!this.#lastFocused) {
            this.#timerPaused = true;
            if (e.relatedTarget && e.relatedTarget.focus) {
                this.#lastFocused = e.relatedTarget;
            }
        }
    }
    /**
     * Listen to focusout, not blur, as blur does not bubble.
     *
     * Unpause the countdown by clearing this.#lastFocused
     * if the focus is moving outside of the snackbar item.
     */
    #onFocusOut(e) {
        const snackbarItemLostFocus = !e.relatedTarget ||
            !this.shadowRoot?.host.contains(e.relatedTarget);
        if (snackbarItemLostFocus) {
            this.#timerPaused = false;
            this.#lastFocused = null;
        }
    }
    #onMouseEnter() {
        this.#timerPaused = true;
    }
    #onMouseLeave() {
        // Unpause unless we still have keyboard focus
        if (!this.#lastFocused) {
            this.#timerPaused = false;
        }
    }
    #hasAction() {
        const actionSlot = this.shadowRoot?.querySelector('slot[name="action"]');
        if (!actionSlot)
            return false;
        const assignedElements = actionSlot.assignedElements();
        return assignedElements.length > 0;
    }
    firstUpdated() {
        // The slot must render before we can check its contents, hence the run on firstUpdated
        this.hasAction = this.#hasAction();
    }
    render() {
        if (this.hasAction &&
            document.documentElement.dataset.wKeyboardinteraction === "true") {
            const modifier = window.navigator.userAgent.includes("Macintosh")
                ? "⌥"
                : "Alt";
            return html `
				<w-attention tooltip placement="top" distance="12" show>
					<div class="animation-wrapper" slot="target">
						<div
							part="item"
							@mouseenter=${this.#onMouseEnter}
							@mouseleave=${this.#onMouseLeave}
							@focusin=${this.#onFocusIn}
							@focusout=${this.#onFocusOut}
						>
							<div part="icon">
								<slot name="icon"></slot>
							</div>
							<div part="message">
								<slot></slot>
							</div>
							<div part="action">
								<slot name="action"></slot>
								<slot name="close"></slot>
							</div>
						</div>
					</div>
					<!-- The keyboard shortcut is announced as part of the action button label -->
					<span slot="message" aria-hidden="true">
						<kbd>${modifier}</kbd> + <kbd>Enter</kbd>
					</span>
				</w-attention>
			`;
        }
        return html `
			<div class="animation-wrapper">
				<div
					part="item"
					@mouseenter=${this.#onMouseEnter}
					@mouseleave=${this.#onMouseLeave}
					@focusin=${this.#onFocusIn}
					@focusout=${this.#onFocusOut}
				>
					<div part="icon">
						<slot name="icon"></slot>
					</div>
					<div part="message">
						<slot></slot>
					</div>
					<div part="action">
						<slot name="action"></slot>
						<slot name="close"></slot>
					</div>
				</div>
			</div>
		`;
    }
}
__decorate([
    property({ attribute: "action-placement", reflect: true, useDefault: true })
], WarpSnackbarItem.prototype, "actionPlacement", void 0);
__decorate([
    property({ type: Number, reflect: true, useDefault: true })
], WarpSnackbarItem.prototype, "duration", void 0);
__decorate([
    query(".animation-wrapper")
], WarpSnackbarItem.prototype, "root", void 0);
__decorate([
    state()
], WarpSnackbarItem.prototype, "hasAction", void 0);
if (!customElements.get("w-snackbar-item")) {
    customElements.define("w-snackbar-item", WarpSnackbarItem);
}
//# sourceMappingURL=snackbar-item.js.map